
import { Button } from "./ui/button";
import { useState, useEffect, type ComponentType } from "react";
import { useNavigate, useLocation } from 'react-router-dom';
import { getTabForPath, getPathForTab } from '@/config/routes';
import { useSectionConfig } from '@/context/SectionConfigContext';
import { useConsentOptional } from '@/context/ConsentContext';
import artistIntroLogo from "../assets/artist_intro.svg";
import {
  Megaphone,
  Sparkles,
  User,
  CalendarDays,
  Play,
  ImageIcon,
  Trophy,
  Share2,
  Tags,
  Medal,
  HardDrive,
  Palette,
  MonitorCog,
  FolderTree,
  Users,
  Globe,
  Building2,
  ChevronDown,
  X,
  FileArchive,
  MessageSquare,
  Star,
  Quote,
  Handshake,
  UsersRound,
  BriefcaseBusiness,
} from 'lucide-react';

interface User {
  _id: string;
  username: string;
  user_type: string;
  email: string;
  domain: string;
}

interface Domain {
  _id: string;
  slug: string;
  url: string;
  name?: string;
}

interface SidebarProps {
  user: User | null;
  domains: Domain[];
  selectedDomain: string;
  setSelectedDomain: (domain: string) => void;
  tab: string;
  setTab: (tab: string) => void;
  siteName: string;
  handleLogout: () => void;
  mobileOpen?: boolean;
  onMobileClose?: () => void;
  /** True when the user belongs to >= 1 organization. Controls org section visibility. */
  hasOrganizations?: boolean;
  /** Site type of the SELECTED website: 'artist' (default) | 'management'.
   *  Drives which section managers the nav shows. */
  siteType?: 'artist' | 'management';
}

interface NavItemDef {
  key: string;
  label: string;
  icon: ComponentType<{ className?: string }>;
  requiresDomain: boolean;
  adminOnly: boolean;
  /** Only shown when a management website is selected. */
  managementOnly?: boolean;
}

const navSections: { section: string; items: NavItemDef[] }[] = [
  {
    section: 'Content Sections',
    items: [
      { key: 'banner', label: 'Promo Banner', icon: Megaphone, requiresDomain: true, adminOnly: false },
      { key: 'heroSection', label: 'Hero Section', icon: Sparkles, requiresDomain: true, adminOnly: false },
      { key: 'aboutSection', label: 'About Section', icon: User, requiresDomain: true, adminOnly: false },
      { key: 'toursEvents', label: 'Tours & Events', icon: CalendarDays, requiresDomain: true, adminOnly: false },
      { key: 'music', label: 'Showcase', icon: Play, requiresDomain: true, adminOnly: false },
      // { key: 'video', label: 'Videos', icon: Video, requiresDomain: true, adminOnly: false },
      { key: 'image', label: 'Image Gallery', icon: ImageIcon, requiresDomain: true, adminOnly: false },
      { key: 'awardsSection', label: 'Awards Section', icon: Trophy, requiresDomain: true, adminOnly: false },
      { key: 'infoKit', label: 'Info Kit', icon: FileArchive, requiresDomain: true, adminOnly: false },
      // Management-only section managers — a management website shows the
      // FULL artist section set plus these five.
      { key: 'services', label: 'Services', icon: BriefcaseBusiness, requiresDomain: true, adminOnly: false, managementOnly: true },
      { key: 'artistRoster', label: 'Artist Roster', icon: Star, requiresDomain: true, adminOnly: false, managementOnly: true },
      { key: 'testimonials', label: 'Testimonials', icon: Quote, requiresDomain: true, adminOnly: false, managementOnly: true },
      { key: 'clients', label: 'Our Clients', icon: Handshake, requiresDomain: true, adminOnly: false, managementOnly: true },
      { key: 'team', label: 'Team', icon: UsersRound, requiresDomain: true, adminOnly: false, managementOnly: true },
      { key: 'contactSection', label: 'Contact', icon: MessageSquare, requiresDomain: true, adminOnly: false },
      { key: 'platforms', label: 'Platforms', icon: Share2, requiresDomain: true, adminOnly: false },
    ]
  },
  {
    section: 'Content Settings',
    items: [
      { key: 'category', label: 'Categories', icon: Tags, requiresDomain: true, adminOnly: false },
    ]
  },
  {
    section: 'Super Admin Settings',
    items: [
      { key: 'awardAssets', label: 'Award Asset Library', icon: Medal, requiresDomain: false, adminOnly: true },
      { key: 'serverImages', label: 'Server Media', icon: HardDrive, requiresDomain: false, adminOnly: true },
      { key: 'masterTheme', label: 'Master Theme Management', icon: Palette, requiresDomain: false, adminOnly: true },
      { key: 'masterPlatforms', label: 'Master Platform Management', icon: MonitorCog, requiresDomain: false, adminOnly: true },
      { key: 'masterCategory', label: 'Master Category Management', icon: FolderTree, requiresDomain: false, adminOnly: true },
      { key: 'user', label: 'Users', icon: Users, requiresDomain: false, adminOnly: true },
      { key: 'domain', label: 'Domains', icon: Globe, requiresDomain: false, adminOnly: true },
      { key: 'organizations', label: 'Management Companies', icon: Building2, requiresDomain: false, adminOnly: true },
    ]
  }
];

// Add styles for smooth collapse animation
const styles = `
  @keyframes slideDown {
    from {
      opacity: 0;
      transform: translateY(-8px);
      max-height: 0;
    }
    to {
      opacity: 1;
      transform: translateY(0);
      max-height: 500px;
    }
  }

  @keyframes slideUp {
    from {
      opacity: 1;
      transform: translateY(0);
      max-height: 500px;
    }
    to {
      opacity: 0;
      transform: translateY(-8px);
      max-height: 0;
    }
  }

  .sidebar-section-content {
    animation: slideDown 0.3s cubic-bezier(0.4, 0, 0.2, 1);
    overflow: hidden;
  }

  .sidebar-section-content.collapsing {
    animation: slideUp 0.3s cubic-bezier(0.4, 0, 0.2, 1) forwards;
  }
`;

// @ts-ignore - These props are passed from parent but not used in this component
const Sidebar: React.FC<SidebarProps> = ({
  user,
  domains,
  selectedDomain,
  setSelectedDomain,
  tab: _tabProp,
  setTab: _setTabProp,
  siteName,
  handleLogout: _handleLogout,
  mobileOpen = false,
  onMobileClose,
  hasOrganizations: _hasOrganizations = false,
  siteType = 'artist',
}) => {
  const navigate = useNavigate();
  const location = useLocation();
  const tab = getTabForPath(location.pathname);
  const setTab = (key: string) => {
    navigate(getPathForTab(key));
    onMobileClose?.();
  };
  const [collapsedSections, setCollapsedSections] = useState<Record<string, boolean>>({
    'Content Sections': false,
    'Content Settings': true,
    'Workspace': false,
    'Super Admin Settings': true,
  });
  const [collapsingAnimations, setCollapsingAnimations] = useState<Record<string, boolean>>({});
  const [mobileDomainOpen, setMobileDomainOpen] = useState(false);
  
  // Section visibility from shared context (no duplicate API calls)
  const { toursVisible, awardsVisible } = useSectionConfig();

  // Close mobile domain dropdown when sidebar closes
  useEffect(() => {
    if (!mobileOpen) setMobileDomainOpen(false);
  }, [mobileOpen]);

  // Load collapsed state from localStorage
  useEffect(() => {
    const stored = localStorage.getItem('sidebarCollapsedSections');
    if (stored) {
      try {
        // Phase 3.5 — merge stored values onto defaults so newly-added
        // sections (e.g. 'Workspace') always have a defined initial state
        // for users with stale localStorage from earlier versions.
        const parsed = JSON.parse(stored);
        setCollapsedSections(prev => ({ ...prev, ...parsed }));
      } catch (e) {
        // If parsing fails, use defaults
      }
    }
  }, []);

  // Auto-expand the section containing the active tab
  useEffect(() => {
    const activeItem = navSections.find(section =>
      section.items.some(item => item.key === tab)
    );

    if (activeItem) {
      setCollapsedSections(prev => ({
        ...prev,
        [activeItem.section]: false, // Expand the active section
      }));
    }
  }, [tab]);

  // Inject styles on mount
  useEffect(() => {
    const styleSheet = document.createElement("style");
    styleSheet.textContent = styles;
    document.head.appendChild(styleSheet);
    return () => styleSheet.remove();
  }, []);

  // Accordion-style toggle with animation support
  const toggleSection = (sectionName: string) => {
    if (!collapsedSections[sectionName]) {
      // Starting collapse animation
      setCollapsingAnimations(prev => ({ ...prev, [sectionName]: true }));
      setTimeout(() => {
        setCollapsedSections(prev => {
          const updated = { ...prev, [sectionName]: true };
          localStorage.setItem('sidebarCollapsedSections', JSON.stringify(updated));
          return updated;
        });
        setCollapsingAnimations(prev => ({ ...prev, [sectionName]: false }));
      }, 300);
    } else {
      // Expanding - collapse all other sections
      const updated: Record<string, boolean> = {};
      navSections.forEach(section => {
        updated[section.section] = section.section !== sectionName;
      });
      setCollapsedSections(updated);
      localStorage.setItem('sidebarCollapsedSections', JSON.stringify(updated));
    }
  };
  return (
    <>
      {/* Mobile backdrop */}
      {mobileOpen && (
        <div
          className="fixed inset-0 bg-black/40 backdrop-blur-sm z-[51] lg:hidden"
          onClick={onMobileClose}
        />
      )}
      <aside className={`fixed left-0 top-0 h-screen w-[80%] lg:w-64 bg-sidebar border-r border-sidebar-border flex flex-col justify-between p-5 z-[52] transition-transform duration-300 ease-in-out ${
        mobileOpen ? 'translate-x-0' : '-translate-x-full'
      } lg:translate-x-0 lg:z-40`}>
      <div className="overflow-y-auto flex-1">
        <div className="mb-8 px-3 flex items-center justify-between">
          <a href="/" onClick={(e) => { e.preventDefault(); setTab('dashboard'); }} className="inline-block">
            <img src={artistIntroLogo} alt="Artist Intro" className="h-7 lg:h-10 mb-2 cursor-pointer" />
          </a>
          <button
            onClick={onMobileClose}
            className="lg:hidden p-1.5 ml-auto -mr-1 rounded-md hover:bg-sidebar-accent transition-colors text-muted-foreground hover:text-foreground"
            aria-label="Close menu"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Mobile domain selector (hidden on desktop - it's in TopBar) */}
        {user?.user_type !== 'domain-admin' && domains.length > 0 && (
          <div className="lg:hidden mb-4 px-1 relative">
            <button
              onClick={() => setMobileDomainOpen(!mobileDomainOpen)}
              className="w-full rounded-md border border-sidebar-border bg-sidebar px-3 py-2.5 text-sm font-medium text-foreground flex items-center justify-between gap-2 transition-colors hover:border-primary/40"
            >
              <span className={selectedDomain ? 'text-foreground' : 'text-muted-foreground'}>
                {selectedDomain || 'Select Domain'}
              </span>
              <ChevronDown className={`w-4 h-4 text-muted-foreground shrink-0 transition-transform duration-200 ${mobileDomainOpen ? 'rotate-180' : ''}`} />
            </button>
            {mobileDomainOpen && (
              <div className="absolute top-full left-0 right-0 mt-1 bg-card border border-border rounded-md shadow-lg z-10 max-h-52 overflow-y-auto">
                <button
                  onClick={() => { setSelectedDomain(''); setMobileDomainOpen(false); }}
                  className={`w-full text-left px-3 py-2.5 text-sm font-medium transition-colors ${
                    !selectedDomain ? 'bg-primary/10 text-primary' : 'text-foreground hover:bg-muted/50'
                  }`}
                >
                  Select Domain
                </button>
                {domains.map(d => (
                  <button
                    key={d._id}
                    onClick={() => { setSelectedDomain(d.slug); setMobileDomainOpen(false); }}
                    className={`w-full text-left px-3 py-2.5 text-sm font-medium transition-colors ${
                      selectedDomain === d.slug ? 'bg-primary/10 text-primary' : 'text-foreground hover:bg-muted/50'
                    }`}
                  >
                    {d.slug}
                  </button>
                ))}
              </div>
            )}
          </div>
        )}
        {user?.user_type === 'domain-admin' && (
          <div className="lg:hidden mb-4 px-1">
            <div className="rounded-md bg-sidebar-accent px-3 py-2 text-sm font-semibold text-foreground">
              {siteName || 'Domain'}
            </div>
          </div>
        )}
        
        {/* Dashboard - Standalone */}
        <Button
          variant={tab === 'dashboard' ? "default" : "ghost"}
          className={`w-full justify-start text-left mb-4 cursor-pointer h-10 px-3 text-sm font-semibold rounded-md transition-all duration-200 ${
            tab === 'dashboard' 
              ? 'bg-sidebar-accent text-primary shadow-none hover:bg-sidebar-accent active:bg-sidebar-accent'
              : 'text-sidebar-foreground hover:text-primary hover:bg-sidebar-hover'
          }`}
          onClick={() => setTab('dashboard')}
        >
          <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2V6zM14 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2V6zM4 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2v-2zM14 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2v-2z" />
          </svg>
          Dashboard
        </Button>

        <nav className="space-y-1">
          {navSections.map((section) => {
            const visibleItems = section.items.filter(item => {
              // Hide admin-only items for non-super-admin users
              if (item.adminOnly && user?.user_type !== 'super-admin') return false;
              
              // Hide domain-dependent items when no domain is selected (for super-admin)
              if (item.requiresDomain && user?.user_type === 'super-admin' && !selectedDomain) return false;
              
              // Dynamic settings scoping: management websites get the full
              // artist section set PLUS the management-only managers
              // (roster/testimonials/clients/team).
              if (item.managementOnly && siteType !== 'management') return false;
              
              // Hide Tours & Events if section is not visible on website
              if (item.key === 'toursEvents' && !toursVisible) return false;
              
              // Hide Awards Section if section is not visible on website
              if (item.key === 'awardsSection' && !awardsVisible) return false;
              
              return true;
            });

            // Don't render section if all items are hidden
            if (visibleItems.length === 0) return null;

            // Check if any item in this section is active
            const isSectionActive = visibleItems.some(item => item.key === tab);

            return (
              <div key={section.section} className="mb-2">
                <button
                  onClick={() => toggleSection(section.section)}
                  className={`group w-full flex items-center justify-between text-xs font-semibold uppercase tracking-wider px-3 py-2 mb-1 rounded-md transition-all duration-200 hover:text-sidebar-foreground ${
                    isSectionActive 
                      ? 'text-foreground' 
                      : 'text-sidebar-foreground/60'
                  }`}
                >
                  <span className="transition-colors duration-200">{section.section}</span>
                  <svg
                    className={`w-3.5 h-3.5 transition-transform duration-300 ease-out opacity-60 ${
                      collapsedSections[section.section] ? '-rotate-90' : ''
                    }`}
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                  </svg>
                </button>
                {(!collapsedSections[section.section] || collapsingAnimations[section.section]) && (
                  <div 
                    className={`sidebar-section-content space-y-0.5 ${
                      collapsingAnimations[section.section] ? 'collapsing' : ''
                    }`}
                  >
                    {visibleItems.map(item => (
                      <Button
                        key={item.key}
                        variant={tab === item.key ? "default" : "ghost"}
                        className={`w-full justify-start text-left transition-all duration-200 cursor-pointer h-9 px-3 text-sm font-semibold rounded-md  ${
                          tab === item.key 
                            ? 'bg-sidebar-accent text-primary shadow-none hover:bg-sidebar-accent active:bg-sidebar-accent'
                            : 'text-sidebar-foreground hover:text-primary hover:bg-sidebar-hover'
                        }`}
                        onClick={() => setTab(item.key as any)}
                      >
                        {item.icon && <item.icon className="w-4 h-4 mr-2 shrink-0" />}
                        {item.label}
                      </Button>
                    ))}
                  </div>
                )}
              </div>
            );
          })}
        </nav>

        {/* Mobile-only quick links (hidden on desktop since they're in TopBar) */}
        {selectedDomain && (
          <div className="lg:hidden mt-4 pt-4 border-t border-sidebar-border space-y-0.5">
            <div className="text-xs font-semibold uppercase tracking-wider text-sidebar-foreground/60 px-3 py-2 mb-1">
              Quick Links
            </div>
            <Button
              variant={tab === 'siteBranding' ? "default" : "ghost"}
              className={`w-full justify-start text-left cursor-pointer h-9 px-3 text-sm font-semibold rounded-md transition-all duration-200 ${
                tab === 'siteBranding'
                  ? 'bg-sidebar-accent text-primary shadow-none hover:bg-sidebar-accent'
                  : 'text-sidebar-foreground hover:text-primary hover:bg-sidebar-hover'
              }`}
              onClick={() => setTab('siteBranding')}
            >
              <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.066 2.573c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.573 1.066c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.066-2.573c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
              </svg>
              Website Settings
            </Button>
            <Button
              variant={tab === 'contactWebsite' ? "default" : "ghost"}
              className={`w-full justify-start text-left cursor-pointer h-9 px-3 text-sm font-semibold rounded-md transition-all duration-200 ${
                tab === 'contactWebsite'
                  ? 'bg-sidebar-accent text-primary shadow-none hover:bg-sidebar-accent'
                  : 'text-sidebar-foreground hover:text-primary hover:bg-sidebar-hover'
              }`}
              onClick={() => setTab('contactWebsite')}
            >
              <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
              </svg>
              Website Leads
            </Button>
          </div>
        )}
      </div>
      <div className="space-y-2 shrink-0">
        <div className="border-t border-sidebar-border pt-4 px-3">
          <div className="text-sm text-muted-foreground text-center flex flex-col items-center gap-1">
            <span>© {new Date().getFullYear()} artist intro</span>
            <PrivacyChoicesLink />
          </div>
        </div>
      </div>
    </aside>
    </>
  );
};

/**
 * Small text link that re-opens the cookie / privacy notice card. Uses the
 * optional consent hook so the sidebar continues to render even if the
 * provider isn't mounted (defensive — the provider is at root today).
 */
const PrivacyChoicesLink = () => {
  const consent = useConsentOptional();
  if (!consent) return null;
  return (
    <button
      type="button"
      onClick={() => consent.openSettings()}
      className="text-xs underline underline-offset-2 hover:text-foreground transition-colors"
    >
      Privacy notice
    </button>
  );
};

export default Sidebar;
